import { useState, useEffect } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Loader2, Mail, Phone, User, AlertTriangle } from "lucide-react";
import { api, formatPrice, formatApiErrorDetail } from "@/lib/api";

// Multi-step clinic registration modal:
// 1. Participant details
// 2. GFWJC waiver (full text + signature)
// 3. ALKAME waiver (full text + signature)
// 4. Review → Square checkout
export default function ClinicRegister({ open, onClose, clinic, slug }) {
  const [step, setStep] = useState(1);
  const [gfwjcWaiver, setGfwjcWaiver] = useState({ text: "", version: "" });
  const [submitting, setSubmitting] = useState(false);
  const [waitlistMode, setWaitlistMode] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    emergency_contact_name: "", emergency_contact_phone: "",
    fitness_experience: "", injuries_notes: "", training_considerations: "",
    age_confirmed: false,
    gfwjc_waiver_accepted: false, gfwjc_signature_name: "",
    alkame_waiver_accepted: false, alkame_signature_name: "",
  });

  useEffect(() => {
    if (open && slug) {
      api.get(`/clinics/${slug}/waiver/gfwjc`).then((r) => setGfwjcWaiver(r.data)).catch(() => {});
      setStep(1);
      setWaitlistMode(clinic?.available === 0);
    }
  }, [open, slug, clinic?.available]);

  if (!open || !clinic) return null;

  const canStep1Next = form.first_name && form.last_name && form.email && form.phone
    && form.emergency_contact_name && form.emergency_contact_phone && form.age_confirmed;
  const canStep2Next = form.gfwjc_waiver_accepted && form.gfwjc_signature_name.trim();
  const canStep3Next = form.alkame_waiver_accepted && form.alkame_signature_name.trim();

  const missingStep1 = () => {
    const m = [];
    if (!form.first_name.trim()) m.push("first name");
    if (!form.last_name.trim()) m.push("last name");
    if (!form.email.trim()) m.push("email");
    if (!form.phone.trim()) m.push("phone");
    if (!form.emergency_contact_name.trim()) m.push("emergency contact name");
    if (!form.emergency_contact_phone.trim()) m.push("emergency contact phone");
    if (!form.age_confirmed) m.push("18+ confirmation checkbox");
    return m;
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const joinWaitlist = async () => {
    if (!form.first_name || !form.last_name || !form.email) {
      toast("Please provide name and email");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/clinics/${slug}/waitlist`, {
        first_name: form.first_name, last_name: form.last_name,
        email: form.email, phone: form.phone,
      });
      toast("You're on the waitlist! We'll email you if a spot opens.");
      onClose();
    } catch (e) {
      toast(formatApiErrorDetail(e.response?.data?.detail) || "Could not join waitlist.");
    } finally { setSubmitting(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/clinics/${slug}/register`, form);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast("Registration created. Coach will contact you to complete payment.", { duration: 6000 });
        onClose();
      }
    } catch (e) {
      toast(formatApiErrorDetail(e.response?.data?.detail) || "Could not complete registration.");
    } finally { setSubmitting(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
          data-testid="clinic-register-modal"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
            className="relative w-full sm:max-w-2xl bg-[#0a0a0a] border border-white/10 my-0 sm:my-8 min-h-screen sm:min-h-0"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 px-5 sm:px-7 py-4 flex items-center justify-between z-10">
              <div>
                <div className="font-mono-accent text-[10px] text-[#005EEA] tracking-[0.3em]">
                  {waitlistMode ? "— WAITLIST" : `— STEP ${step} / 4`}
                </div>
                <h3 className="font-display text-xl sm:text-2xl uppercase leading-tight mt-0.5">
                  {waitlistMode ? "Join Waitlist" : "Register — Power the Paddle"}
                </h3>
              </div>
              <button onClick={onClose} aria-label="Close" data-testid="clinic-register-close" className="text-white/40 hover:text-white transition-colors">
                <X size={22} />
              </button>
            </div>

            <div className="px-5 sm:px-7 py-6 sm:py-8 pb-28 sm:pb-8">
              {waitlistMode ? (
                <WaitlistView form={form} update={update} submit={joinWaitlist} submitting={submitting} />
              ) : (
                <>
                  {step === 1 && <Step1Participant form={form} update={update} />}
                  {step === 2 && <Step2Waiver kind="gfwjc" title="Getfitwithjeanclaude — Participant Waiver" text={gfwjcWaiver.text} form={form} update={update} />}
                  {step === 3 && <Step3Waiver clinic={clinic} form={form} update={update} />}
                  {step === 4 && <Step4Review form={form} clinic={clinic} />}
                </>
              )}
            </div>

            {/* Footer actions */}
            {!waitlistMode && (
              <div className="sticky bottom-0 bg-[#0a0a0a] border-t border-white/10 px-5 sm:px-7 py-4">
                {step === 1 && !canStep1Next && (
                  <p className="font-mono-accent text-[10px] text-yellow-400/90 uppercase tracking-wider mb-2 text-right" data-testid="step1-missing-hint">
                    Missing: {missingStep1().join(", ")}
                  </p>
                )}
                {step === 2 && !canStep2Next && (
                  <p className="font-mono-accent text-[10px] text-yellow-400/90 uppercase tracking-wider mb-2 text-right" data-testid="step2-missing-hint">
                    Sign the waiver: type your name and check the box
                  </p>
                )}
                {step === 3 && !canStep3Next && (
                  <p className="font-mono-accent text-[10px] text-yellow-400/90 uppercase tracking-wider mb-2 text-right" data-testid="step3-missing-hint">
                    Sign the ALKAME waiver: type your name and check the box
                  </p>
                )}
                <div className="flex items-center justify-between gap-3">
                {step > 1 ? (
                  <button
                    onClick={() => setStep(step - 1)}
                    className="font-mono-accent text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors inline-flex items-center gap-2"
                    data-testid="clinic-register-back"
                  >
                    <ArrowLeft size={13} /> Back
                  </button>
                ) : <span />}
                {step < 4 ? (
                  <button
                    onClick={() => setStep(step + 1)}
                    disabled={(step === 1 && !canStep1Next) || (step === 2 && !canStep2Next) || (step === 3 && !canStep3Next)}
                    className="font-mono-accent text-xs uppercase tracking-[0.2em] bg-[#005EEA] text-white px-5 py-3 hover:bg-[#0047b3] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    data-testid="clinic-register-next"
                  >
                    Continue <ArrowRight size={13} />
                  </button>
                ) : (
                  <button
                    onClick={submit}
                    disabled={submitting}
                    className="font-mono-accent text-xs uppercase tracking-[0.2em] bg-[#005EEA] text-white px-6 py-3 hover:bg-[#0047b3] transition-colors flex items-center gap-2 shadow-[0_0_30px_rgba(0,94,234,0.4)] disabled:opacity-60"
                    data-testid="clinic-register-pay"
                  >
                    {submitting ? <><Loader2 size={13} className="animate-spin" /> Opening checkout</> : <>Pay {formatPrice(clinic.total_cents, clinic.currency)} <ArrowRight size={13} /></>}
                  </button>
                )}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ---- Steps ----
function Field({ label, value, onChange, required, type = "text", placeholder, testid, ...rest }) {
  return (
    <div>
      <label className="font-mono-accent text-[10px] text-white/40 block mb-1.5 uppercase tracking-wider">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-black border border-white/15 px-3 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#005EEA] transition-colors"
        {...rest}
      />
    </div>
  );
}

function Step1Participant({ form, update }) {
  return (
    <div className="space-y-4" data-testid="clinic-step-1">
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name" value={form.first_name} onChange={(v) => update("first_name", v)} required testid="reg-first-name" />
        <Field label="Last name" value={form.last_name} onChange={(v) => update("last_name", v)} required testid="reg-last-name" />
      </div>
      <Field label="Email" type="email" value={form.email} onChange={(v) => update("email", v)} required testid="reg-email" />
      <Field label="Phone" type="tel" value={form.phone} onChange={(v) => update("phone", v)} required testid="reg-phone" />
      <div className="pt-4 border-t border-white/10">
        <p className="font-mono-accent text-[10px] text-[#005EEA] mb-3 tracking-wider">EMERGENCY CONTACT</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" value={form.emergency_contact_name} onChange={(v) => update("emergency_contact_name", v)} required testid="reg-emergency-name" />
          <Field label="Phone" type="tel" value={form.emergency_contact_phone} onChange={(v) => update("emergency_contact_phone", v)} required testid="reg-emergency-phone" />
        </div>
      </div>
      <div className="pt-4 border-t border-white/10 space-y-4">
        <p className="font-mono-accent text-[10px] text-[#005EEA] tracking-wider">BACKGROUND</p>
        <div>
          <label className="font-mono-accent text-[10px] text-white/40 block mb-1.5 uppercase tracking-wider">Fitness / exercise experience</label>
          <textarea value={form.fitness_experience} onChange={(e) => update("fitness_experience", e.target.value)}
            rows={2} data-testid="reg-fitness-exp"
            placeholder="e.g. 2 years recreational paddling, light gym work"
            className="w-full bg-black border border-white/15 px-3 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#005EEA] transition-colors" />
        </div>
        <div>
          <label className="font-mono-accent text-[10px] text-white/40 block mb-1.5 uppercase tracking-wider">Injuries or physical limitations</label>
          <textarea value={form.injuries_notes} onChange={(e) => update("injuries_notes", e.target.value)}
            rows={2} data-testid="reg-injuries"
            placeholder="Anything relevant — past surgeries, chronic pain, etc. Say 'none' if clear."
            className="w-full bg-black border border-white/15 px-3 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#005EEA] transition-colors" />
        </div>
        <div>
          <label className="font-mono-accent text-[10px] text-white/40 block mb-1.5 uppercase tracking-wider">Other training considerations</label>
          <textarea value={form.training_considerations} onChange={(e) => update("training_considerations", e.target.value)}
            rows={2} data-testid="reg-considerations"
            placeholder="Optional — anything coach should know"
            className="w-full bg-black border border-white/15 px-3 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#005EEA] transition-colors" />
        </div>
      </div>
      <label className="flex items-start gap-2 pt-4 border-t border-white/10 cursor-pointer">
        <input type="checkbox" checked={form.age_confirmed} onChange={(e) => update("age_confirmed", e.target.checked)}
          className="accent-[#005EEA] w-4 h-4 mt-0.5" data-testid="reg-age" />
        <span className="text-white/80 text-sm">I confirm I am at least 18 years old.</span>
      </label>
    </div>
  );
}

function Step2Waiver({ title, text, form, update }) {
  return (
    <div className="space-y-4" data-testid="clinic-step-2">
      <p className="font-mono-accent text-[10px] text-[#005EEA] tracking-wider">WAIVER 1 OF 2</p>
      <h4 className="font-display text-xl uppercase leading-tight">{title}</h4>
      <div className="border border-white/10 bg-black/40 p-4 max-h-72 overflow-y-auto whitespace-pre-wrap text-xs text-white/70 leading-relaxed" data-testid="gfwjc-waiver-text">
        {text || "Loading waiver..."}
      </div>
      <Field label="Type your full legal name as signature" value={form.gfwjc_signature_name}
        onChange={(v) => update("gfwjc_signature_name", v)} required testid="reg-gfwjc-signature"
        placeholder="e.g. Jane Alexandra Doe" />
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" checked={form.gfwjc_waiver_accepted} onChange={(e) => update("gfwjc_waiver_accepted", e.target.checked)}
          className="accent-[#005EEA] w-4 h-4 mt-0.5" data-testid="reg-gfwjc-accept" />
        <span className="text-white/85 text-sm">
          I have read and agree to the Getfitwithjeanclaude Participant Liability & Training Waiver.
          By typing my name and checking this box, I provide my electronic signature.
        </span>
      </label>
    </div>
  );
}

function Step3Waiver({ clinic, form, update }) {
  const hasExternalUrl = Boolean(clinic.alkame_waiver_url);
  return (
    <div className="space-y-4" data-testid="clinic-step-3">
      <p className="font-mono-accent text-[10px] text-[#005EEA] tracking-wider">WAIVER 2 OF 2</p>
      <h4 className="font-display text-xl uppercase leading-tight">ALKAME / PaddleZone Facility Waiver</h4>
      {hasExternalUrl ? (
        <>
          <div className="border border-[#005EEA]/30 bg-[#005EEA]/[0.08] p-4" data-testid="alkame-waiver-instructions">
            <p className="font-mono-accent text-[10px] text-[#005EEA] mb-2 tracking-wider">EXTERNAL FORM</p>
            <p className="text-white/80 text-sm">
              ALKAME uses an external form for their facility waiver. Click the button below,
              complete and submit it on their site, then return here to continue.
            </p>
          </div>
          <a href={clinic.alkame_waiver_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#005EEA] text-white px-5 py-4 font-mono-accent text-xs uppercase tracking-wider hover:bg-[#0047b3] transition-colors shadow-[0_0_30px_rgba(0,94,234,0.3)]"
            data-testid="alkame-waiver-link">
            Open & Complete ALKAME Waiver <ArrowRight size={13} />
          </a>
        </>
      ) : (
        <div className="border border-yellow-400/30 bg-yellow-400/5 p-4 flex gap-3" data-testid="alkame-waiver-placeholder">
          <AlertTriangle className="text-yellow-400 shrink-0 mt-0.5" size={18} />
          <div>
            <p className="font-mono-accent text-[10px] text-yellow-400 mb-2 tracking-wider">PLACEHOLDER</p>
            <p className="text-white/70 text-sm">
              The official ALKAME / PaddleZone facility waiver is being finalized and will be
              provided to you via email before the first session. By checking the box below,
              you commit to signing it before your first session.
            </p>
          </div>
        </div>
      )}
      <div className="border border-white/10 bg-black/40 p-4 max-h-40 overflow-y-auto whitespace-pre-wrap text-xs text-white/70 leading-relaxed" data-testid="alkame-waiver-text">
        {clinic.alkame_waiver_text || "ALKAME / PaddleZone will provide their standard facility waiver separately."}
      </div>
      <Field label="Type your full legal name" value={form.alkame_signature_name}
        onChange={(v) => update("alkame_signature_name", v)} required testid="reg-alkame-signature"
        placeholder="e.g. Jane Alexandra Doe" />
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" checked={form.alkame_waiver_accepted} onChange={(e) => update("alkame_waiver_accepted", e.target.checked)}
          className="accent-[#005EEA] w-4 h-4 mt-0.5" data-testid="reg-alkame-accept" />
        <span className="text-white/85 text-sm">
          {hasExternalUrl
            ? "I confirm I have opened and completed the ALKAME / PaddleZone facility waiver via the button above."
            : "I acknowledge and commit to signing the ALKAME / PaddleZone facility waiver prior to participating in the clinic."}
        </span>
      </label>
    </div>
  );
}

function Step4Review({ form, clinic }) {
  return (
    <div className="space-y-5" data-testid="clinic-step-4">
      <p className="font-mono-accent text-[10px] text-[#005EEA] tracking-wider">REVIEW & PAY</p>
      <h4 className="font-display text-xl uppercase leading-tight">You&apos;re one click away.</h4>
      <div className="border border-white/10 bg-black/40 p-5 space-y-3 text-sm">
        <Row icon={User} label="Participant">{form.first_name} {form.last_name}</Row>
        <Row icon={Mail} label="Email">{form.email}</Row>
        <Row icon={Phone} label="Phone">{form.phone}</Row>
        <div className="border-t border-white/10 pt-3 mt-3 space-y-2 text-xs text-white/60">
          <div>GFWJC waiver signed: <span className="text-white/85">{form.gfwjc_signature_name}</span></div>
          <div>ALKAME waiver signed: <span className="text-white/85">{form.alkame_signature_name}</span></div>
        </div>
      </div>
      <div className="border-2 border-[#005EEA] bg-[#005EEA]/[0.08] p-5">
        <div className="font-mono-accent text-[10px] text-[#005EEA] mb-3 tracking-wider">YOUR TOTAL</div>
        <div className="space-y-1 text-sm text-white/70">
          <div className="flex justify-between"><span>Clinic fee</span><span>{formatPrice(clinic.price_cents, clinic.currency)}</span></div>
          <div className="flex justify-between"><span>HST (13%)</span><span>{formatPrice(clinic.hst_cents, clinic.currency)}</span></div>
          <div className="flex justify-between"><span>Processing fee</span><span>{formatPrice(clinic.fee_cents, clinic.currency)}</span></div>
          <div className="flex justify-between border-t border-white/15 pt-2 mt-2 text-white text-base font-semibold">
            <span>Total</span><span data-testid="review-total">{formatPrice(clinic.total_cents, clinic.currency)}</span>
          </div>
        </div>
      </div>
      <p className="text-xs text-white/40 leading-relaxed">
        Clicking <strong className="text-white">Pay</strong> opens secure Square checkout. Your spot is held for 10 minutes while
        you complete payment. Your registration is only CONFIRMED after successful payment.
      </p>
    </div>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="text-[#005EEA] shrink-0 mt-0.5" size={14} />
      <div>
        <div className="font-mono-accent text-[9px] text-white/40 uppercase tracking-wider">{label}</div>
        <div className="text-white/90">{children}</div>
      </div>
    </div>
  );
}

function WaitlistView({ form, update, submit, submitting }) {
  return (
    <div className="space-y-4" data-testid="clinic-waitlist">
      <p className="text-white/70 text-sm">
        This cohort is full. Drop your info and we&apos;ll email you first the moment a spot opens or when
        the next cohort is announced.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name" value={form.first_name} onChange={(v) => update("first_name", v)} required testid="wl-first" />
        <Field label="Last name" value={form.last_name} onChange={(v) => update("last_name", v)} required testid="wl-last" />
      </div>
      <Field label="Email" type="email" value={form.email} onChange={(v) => update("email", v)} required testid="wl-email" />
      <Field label="Phone (optional)" type="tel" value={form.phone} onChange={(v) => update("phone", v)} testid="wl-phone" />
      <button
        onClick={submit}
        disabled={submitting}
        data-testid="wl-submit"
        className="w-full font-mono-accent text-xs uppercase tracking-[0.2em] bg-[#005EEA] text-white py-4 hover:bg-[#0047b3] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {submitting ? <><Loader2 size={13} className="animate-spin" /> Submitting</> : <>Join Waitlist <ArrowRight size={13} /></>}
      </button>
    </div>
  );
}
